import { useEffect, useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  HiOutlineAdjustmentsHorizontal,
  HiOutlineBolt,
  HiOutlineCheckCircle,
  HiOutlineEye,
  HiOutlineEyeSlash,
  HiOutlineLockClosed,
  HiOutlineMagnifyingGlass,
  HiOutlinePencilSquare,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineUserGroup,
} from "react-icons/hi2";

import PageHeader from "../../../components/ui/pageHeader/PageHeader";
import PageCard from "../../../components/ui/pageCard/PageCard";
import PageLoader from "../../../components/ui/pageLoader/PageLoader";
import PageEmpty from "../../../components/ui/pageEmpty/PageEmpty";

import Modal from "../../../components/ui/modal/Modal";
import ConfirmModal from "../../../components/ui/confirmModal/ConfirmModal";

import FieldConfigForm from "../../../components/forms/fieldConfig/FieldConfigForm";

import usePermissions from "../../../hooks/usePermissions";

import {
  deleteFieldConfig,
  getFieldConfigs,
  setSelectedFieldConfig,
  toggleFieldConfigActive,
} from "../../../redux/features/fieldConfigs/fieldConfigSlice";

import {
  getSchoolYears,
  setSelectedSchoolYear,
} from "../../../redux/features/schoolYears/schoolYearSlice";

import "./FieldConfigsPage.css";

const STEP_LABELS = {
  aanmelding: "Aanmelding",
  administratie: "Administratie",
  begeleiding: "Begeleiding",
  vdab: "VDAB",
  kld: "KLD",
  afronding: "Afronding",
};

const ROLE_LABELS = {
  POWER_ADMIN: "Admin",
  POWER_INTERNAL_COACH: "Interne coach",
  POWER_EXTERNAL_COACH: "Externe coach",
};

export default function FieldConfigsPage() {
  const dispatch = useDispatch();

  const { can } = usePermissions();

  /**
   * =====================================================
   * STATE
   * =====================================================
   */

  const [search, setSearch] = useState("");

  const [openCreateModal, setOpenCreateModal] = useState(false);

  const [openEditModal, setOpenEditModal] = useState(false);

  const [selectedEditField, setSelectedEditField] = useState(null);

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "Bevestigen",
    variant: "danger",
    onConfirm: null,
  });

  /**
   * =====================================================
   * REDUX
   * =====================================================
   */

  const { fieldConfigs, selectedFieldConfig, isLoading, error } = useSelector(
    (state) => state.fieldConfigs,
  );

  const { schoolYears, selectedSchoolYear } = useSelector(
    (state) => state.schoolYears,
  );

  /**
   * =====================================================
   * LOAD SCHOOL YEARS
   * =====================================================
   */

  useEffect(() => {
    if (!schoolYears?.length) {
      dispatch(getSchoolYears());
    }
  }, [dispatch, schoolYears]);

  /**
   * =====================================================
   * AUTO SELECT
   * =====================================================
   */

  useEffect(() => {
    if (selectedSchoolYear?._id) return;

    if (!schoolYears?.length) return;

    const activeSchoolYear = schoolYears.find(
      (schoolYear) => schoolYear.is_active,
    );

    if (activeSchoolYear) {
      dispatch(setSelectedSchoolYear(activeSchoolYear));
    }
  }, [dispatch, schoolYears, selectedSchoolYear]);

  /**
   * =====================================================
   * LOAD FIELDS
   * =====================================================
   */

  useEffect(() => {
    if (!selectedSchoolYear?._id) return;

    dispatch(
      getFieldConfigs({
        schoolYearId: selectedSchoolYear._id,
      }),
    );
  }, [dispatch, selectedSchoolYear]);

  /**
   * =====================================================
   * FILTER
   * =====================================================
   */

  const filteredFields = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return fieldConfigs;

    return fieldConfigs.filter((field) => {
      return (
        field.label?.toLowerCase().includes(query) ||
        field.key?.toLowerCase().includes(query) ||
        field.step?.toLowerCase().includes(query) ||
        field.group?.toLowerCase().includes(query) ||
        field.type?.toLowerCase().includes(query)
      );
    });
  }, [fieldConfigs, search]);

  /**
   * =====================================================
   * GROUP BY STEP
   * =====================================================
   */

  const groupedSteps = useMemo(() => {
    const grouped = {};

    [...filteredFields]
      .sort((a, b) => {
        const stepCompare = String(a.step || "").localeCompare(
          String(b.step || ""),
        );

        if (stepCompare !== 0) return stepCompare;

        const groupCompare = String(a.group || "").localeCompare(
          String(b.group || ""),
        );

        if (groupCompare !== 0) return groupCompare;

        return (a.order || 0) - (b.order || 0);
      })
      .forEach((field) => {
        const stepKey = field.step || "general";

        if (!grouped[stepKey]) {
          grouped[stepKey] = [];
        }

        grouped[stepKey].push(field);
      });

    return grouped;
  }, [filteredFields]);

  /**
   * =====================================================
   * SELECT
   * =====================================================
   */

  const handleSelectField = (field) => {
    dispatch(setSelectedFieldConfig(field));
  };

  /**
   * =====================================================
   * EDIT
   * =====================================================
   */

  const openEdit = (field) => {
    setSelectedEditField(field);

    setOpenEditModal(true);
  };

  /**
   * =====================================================
   * TOGGLE
   * =====================================================
   */

  const handleToggleActive = async (id) => {
    try {
      await dispatch(toggleFieldConfigActive(id)).unwrap();
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * =====================================================
   * DELETE
   * =====================================================
   */

  const handleDelete = async (id) => {
    try {
      await dispatch(deleteFieldConfig(id)).unwrap();
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * =====================================================
   * CONFIRM
   * =====================================================
   */

  const closeConfirmModal = () => {
    setConfirmModal({
      isOpen: false,
      title: "",
      message: "",
      confirmText: "Bevestigen",
      variant: "danger",
      onConfirm: null,
    });
  };

  const openDeleteConfirm = (field) => {
    setConfirmModal({
      isOpen: true,

      title: "Veld verwijderen?",

      message: "Deze veldconfiguratie wordt permanent verwijderd.",

      confirmText: "Verwijderen",

      variant: "danger",

      onConfirm: async () => {
        await handleDelete(field._id);

        closeConfirmModal();
      },
    });
  };

  const openToggleConfirm = (field) => {
    setConfirmModal({
      isOpen: true,

      title: field.is_active ? "Veld uitschakelen?" : "Veld activeren?",

      message: field.is_active
        ? "Dit veld wordt verborgen."
        : "Dit veld wordt opnieuw actief.",

      confirmText: field.is_active ? "Uitschakelen" : "Activeren",

      variant: field.is_active ? "warning" : "success",

      onConfirm: async () => {
        await handleToggleActive(field._id);

        closeConfirmModal();
      },
    });
  };

  /**
   * =====================================================
   * LOADING
   * =====================================================
   */

  if (isLoading && !fieldConfigs.length) {
    return <PageLoader title="Veldconfiguraties laden..." />;
  }

  return (
    <div className="field-configs-page">
      <PageHeader
        eyebrow="POWER"
        title="Veldconfiguraties"
        description={`Dynamische formulierstructuur voor ${
          selectedSchoolYear?.name || "schooljaar"
        }`}
        actions={
          can("fieldConfig.create") && (
            <button
              className="field-configs-page__create-button"
              onClick={() => setOpenCreateModal(true)}
            >
              <HiOutlinePlus />

              <span>Nieuw veld</span>
            </button>
          )
        }
      />

      {/* SEARCH */}
      <PageCard className="field-configs-page__filters">
        <div className="field-configs-page__search">
          <HiOutlineMagnifyingGlass />

          <input
            type="text"
            placeholder="Zoek op naam, key, stap, type..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </PageCard>

      {error && <div className="field-configs-page__error">{error}</div>}

      {!filteredFields.length ? (
        <PageEmpty
          icon={<HiOutlineAdjustmentsHorizontal />}
          title="Geen velden gevonden"
          description="Geen resultaten gevonden voor deze zoekopdracht."
        />
      ) : (
        <div className="field-configs-page__sections">
          {Object.entries(groupedSteps).map(([stepKey, fields]) => (
            <PageCard key={stepKey} className="field-configs-page__section">
              <div className="field-configs-page__section-header">
                <div>
                  <span>Workflow stap</span>

                  <h2>{STEP_LABELS[stepKey] || stepKey}</h2>
                </div>

                <div className="field-configs-page__section-count">
                  {fields.length} velden
                </div>
              </div>

              <div className="field-configs-page__field-list">
                {fields.map((field) => (
                  <button
                    key={field._id}
                    className={`field-configs-page__field ${
                      selectedFieldConfig?._id === field._id
                        ? "field-configs-page__field--selected"
                        : ""
                    }`}
                    onClick={() => handleSelectField(field)}
                  >
                    <div className="field-configs-page__field-top">
                      <div>
                        <h3>{field.label}</h3>

                        <span>{field.key}</span>
                      </div>

                      <div className="field-configs-page__field-type">
                        {field.type}
                      </div>
                    </div>

                    {field.description && (
                      <p className="field-configs-page__field-description">
                        {field.description}
                      </p>
                    )}

                    <div className="field-configs-page__field-meta">
                      {field.required && (
                        <span className="field-configs-page__badge field-configs-page__badge--required">
                          Verplicht
                        </span>
                      )}

                      {field.read_only && (
                        <span className="field-configs-page__badge field-configs-page__badge--readonly">
                          <HiOutlineLockClosed />
                          Alleen lezen
                        </span>
                      )}

                      {field.hidden ? (
                        <span className="field-configs-page__badge field-configs-page__badge--hidden">
                          <HiOutlineEyeSlash />
                          Verborgen
                        </span>
                      ) : (
                        <span className="field-configs-page__badge field-configs-page__badge--visible">
                          <HiOutlineEye />
                          Zichtbaar
                        </span>
                      )}

                      {field.is_active && (
                        <span className="field-configs-page__badge field-configs-page__badge--active">
                          <HiOutlineCheckCircle />
                          Actief
                        </span>
                      )}
                    </div>

                    <div className="field-configs-page__roles">
                      <div className="field-configs-page__role-box">
                        <HiOutlineUserGroup />

                        <div>
                          <strong>Zichtbaar</strong>

                          <span>
                            {field.visible_for?.length
                              ? field.visible_for
                                  .map((role) => ROLE_LABELS[role])
                                  .join(", ")
                              : "Iedereen"}
                          </span>
                        </div>
                      </div>

                      <div className="field-configs-page__role-box">
                        <HiOutlineUserGroup />

                        <div>
                          <strong>Bewerkbaar</strong>

                          <span>
                            {field.editable_by?.length
                              ? field.editable_by
                                  .map((role) => ROLE_LABELS[role])
                                  .join(", ")
                              : "Iedereen"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="field-configs-page__field-footer">
                      <span>Groep: {field.group || "general"}</span>

                      <span>Volgorde: {field.order || 0}</span>

                      <span>Excel: {field.excel_column || "-"}</span>
                    </div>

                    <div className="field-configs-page__actions">
                      {can("fieldConfig.update") && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();

                            openToggleConfirm(field);
                          }}
                        >
                          <HiOutlineBolt />
                        </button>
                      )}

                      {can("fieldConfig.update") && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();

                            openEdit(field);
                          }}
                        >
                          <HiOutlinePencilSquare />
                        </button>
                      )}

                      {can("fieldConfig.delete") && (
                        <button
                          type="button"
                          className="field-configs-page__delete"
                          onClick={(e) => {
                            e.stopPropagation();

                            openDeleteConfirm(field);
                          }}
                        >
                          <HiOutlineTrash />
                        </button>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </PageCard>
          ))}
        </div>
      )}

      {/* CREATE */}
      <Modal
        isOpen={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        title="Nieuw veld"
        description="Maak een nieuwe dynamische veldconfiguratie."
      >
        <FieldConfigForm
          schoolYearId={selectedSchoolYear?._id}
          mode="create"
          onSuccess={() => setOpenCreateModal(false)}
          onCancel={() => setOpenCreateModal(false)}
        />
      </Modal>

      {/* EDIT */}
      <Modal
        isOpen={openEditModal}
        onClose={() => setOpenEditModal(false)}
        title="Veld wijzigen"
        description="Wijzig de instellingen van dit veld."
      >
        <FieldConfigForm
          schoolYearId={selectedSchoolYear?._id}
          mode="edit"
          initialData={selectedEditField}
          onSuccess={() => setOpenEditModal(false)}
          onCancel={() => setOpenEditModal(false)}
        />
      </Modal>

      {/* CONFIRM */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        variant={confirmModal.variant}
        onClose={closeConfirmModal}
        onConfirm={confirmModal.onConfirm}
      />
    </div>
  );
}
